import { BehaviorSubject, type Observable } from "rxjs";

import { isAbortError, isNetworkError } from "./error";

export enum AuthState {
    Unknown,
    Authenticated,
    NonAuthenticated,
}

export interface SSOTokenStorage {
    readonly key: string;
    setToken: (token: string) => void;
    getToken: () => string;
    removeToken: () => void;
    hasToken: () => boolean;
}

export interface SSOAPIClient {
    logout: () => Promise<Response>;
    refreshToken: () => Promise<Response>;
    redirectToAccountsServer: (referrer?: string) => void;
}

export interface SSOServiceConfig {
    ssoAPIClient: SSOAPIClient;
    ssoTokenStorage: SSOTokenStorage;
    historyPush: (path: string) => void;
}

// TODO: update in https://github.sc-corp.net/Snapchat/web-common/blob/main/packages/sso-auth/src/sso-service/SSOService.ts
export class SSOService {
    private static readonly tokenURLParamKey: string = "ticket";
    private readonly ssoTokenStorage: SSOTokenStorage;
    private readonly historyPush: (path: string) => void;
    private readonly ssoAPIClient: SSOAPIClient;

    /**
     *
     * @param config SSOServiceConfig
     * @param authStateSubject - optional BehaviorSubject that can be used to subscribe to
     * and to read the current value of authentication state.
     * If not provided initialized with AuthState.Unknown
     * @see https://rxjs.dev/guide/subject#behaviorsubject
     */
    constructor(
        config: SSOServiceConfig,
        private readonly authStateSubject = new BehaviorSubject(AuthState.Unknown),
    ) {
        this.ssoTokenStorage = config.ssoTokenStorage;
        this.ssoAPIClient = config.ssoAPIClient;
        this.historyPush = config.historyPush;
        this.authStateSubject = authStateSubject;
    }

    /**
     * @returns an Observable that can be used to subscribe to AuthState
     */
    getAuthStateAsObservable(): Observable<AuthState> {
        return this.authStateSubject.asObservable();
    }

    /**
     * @returns current AuthState
     */
    getCurrentAuthState(): AuthState {
        return this.authStateSubject.getValue();
    }

    /**
     * @returns Promise<{@link AuthState}> - current authentication state
     * after an authentication attempt.
     */
    async tryAuthenticate(): Promise<AuthState> {
        await this.tryGetSessionToken();
        return this.authStateSubject.getValue();
    }

    /**
     * @returns Promise<{@link AuthState}> - current authentication state.
     *
     * Attempts to refresh existing token.
     * In case of success - the token is stored and reflected in the authentication state.
     * In case of error - token is removed, auth state is set to AuthState.NonAuthenticated.
     */
    async refreshToken(): Promise<AuthState> {
        try {
            await this.tryRefreshToken();
        } catch (error) {
            console.error(error);
            this.ssoTokenStorage.removeToken();
            this.authStateSubject.next(AuthState.NonAuthenticated);
        }
        return this.authStateSubject.getValue();
    }

    /**
     * @returns Promise<{@link AuthState}> - current authentication state.
     *
     * Attempts to refresh existing token.
     * In case of success - the token is stored and reflected in the authentication state.
     * In case of HTTP error - token is removed, auth state is set to AuthState.NonAuthenticated.
     * In case of network or abort error - auth state is not changed, client should retry later.
     */
    async backgroundRefreshToken(): Promise<AuthState> {
        try {
            await this.tryRefreshToken();
        } catch (error) {
            console.error(error);
            if (isNetworkError(error) || isAbortError(error)) {
                // network error or abort error
                // since this is background refresh, we shouldnt log user out, instead client should retry later
                return this.authStateSubject.getValue();
            }

            // HTTP error, means either server is down, or user is not authenticated
            // log user out
            this.ssoTokenStorage.removeToken();
            this.authStateSubject.next(AuthState.NonAuthenticated);
        }
        return this.authStateSubject.getValue();
    }

    /**
     * Redirects to accounts server.
     * @param referrer - URL that will used in Location header in response from accounts server
     * after the authentication is completed.
     */
    redirectToAccountsServer(referrer?: string): void {
        this.ssoAPIClient.redirectToAccountsServer(referrer);
    }

    /**
     * After log out by default: Redirects to accounts server.
     *
     * @param referrer - URL that will used in Location header in response from accounts server
     * after the next authentication (if any) is completed.
     * @param customRedirectURL - If provided, after log out this URL will be used to redirect the user.
     * Overrides default redirect to the accounts server.
     */
    async logout(referrer?: string, customRedirectURL?: string): Promise<void> {
        try {
            await this.ssoAPIClient.logout();
        } catch (error) {
            console.error(error);
        } finally {
            this.ssoTokenStorage.removeToken();
            this.authStateSubject.next(AuthState.NonAuthenticated);

            if (customRedirectURL) {
                window.location.assign(customRedirectURL);
            } else {
                this.redirectToAccountsServer(referrer);
            }
        }
    }

    private async tryGetSessionToken() {
        const hashParams = new URLSearchParams(window.location.hash.slice(1));
        const queryParams = new URLSearchParams(window.location.search);
        const ticketInHash = hashParams.has(SSOService.tokenURLParamKey);

        if (ticketInHash || queryParams.has(SSOService.tokenURLParamKey)) {
            await this.refreshToken();
            queryParams.delete(SSOService.tokenURLParamKey);
            const query = queryParams.toString();

            // Only re-serialize the fragment when it carried the ticket: a URLSearchParams
            // round-trip rewrites a plain anchor such as `#section` to `#section=`.
            let fragment = window.location.hash;
            if (ticketInHash) {
                hashParams.delete(SSOService.tokenURLParamKey);
                const hash = hashParams.toString();
                fragment = hash ? `#${hash}` : "";
            }

            this.historyPush(`${window.location.pathname}${query ? `?${query}` : ""}${fragment}`);
        } else if (this.ssoTokenStorage.hasToken()) {
            await this.refreshToken();
        } else {
            this.authStateSubject.next(AuthState.NonAuthenticated);
        }
    }

    private async tryRefreshToken() {
        const response = await this.ssoAPIClient.refreshToken();
        if (!response.ok) {
            throw new Error(response.statusText);
        }

        const responseText = await response.text();
        const isHTMLResponse = /<\/?[a-z][\S\s]*>/i.test(responseText);
        if (isHTMLResponse) {
            throw new Error(`invalid token response: ${responseText}`);
        }

        this.ssoTokenStorage.setToken(responseText);
        this.authStateSubject.next(AuthState.Authenticated);
    }
}
